//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isTruthy } from '@utils/filterPredicates';

interface TypeaheadSearchOption {
  externalID: string;
  name: string;
}

interface TypeaheadDropdownContentProps {
  allowCustomValue: boolean;
  isLoading: boolean;
  onCustomValueSelect: (customValue: string) => void;
  onSearch: (searchTerm: string) => void;
  onSelect: (value: string) => void;
  options: TypeaheadSearchOption[];
  queryError: Error | null;
  resourceName: string;
  searchKeys: string[];
  searchPlaceholder: string;
  trackingIdPrefix: TrackingIdPrefix;
  trimmedSearch: string;
  value: string;
  visuallyHiddenLabel: string;
}

// Caps the portal at 40vh on short viewports (e.g. 1280x551), 320px otherwise.
const DROPDOWN_MAX_HEIGHT = 'min(320px, 40vh)';

export const TypeaheadDropdownContent = ({
  allowCustomValue,
  isLoading,
  onCustomValueSelect,
  onSearch,
  onSelect,
  options,
  queryError,
  resourceName,
  searchKeys,
  searchPlaceholder,
  trackingIdPrefix,
  trimmedSearch,
  value,
  visuallyHiddenLabel,
}: TypeaheadDropdownContentProps) => {
  return (
    <TypeaheadDropdownMenuContent align='start'>
      <DropdownMenu.Search
        handleClear={() => onSearch('')}
        handleSearch={onSearch}
        itemsToSearch={options}
        searchBarConfig={{
          placeholder: searchPlaceholder,
          trackingIdPrefix,
          visuallyHiddenLabel,
        }}
        searchKeys={searchKeys}
      />
      {isLoading && <EmptyState>Loading {resourceName}...</EmptyState>}
      {!isLoading && isTruthy(queryError) && (
        <EmptyState>Error loading {resourceName}. Please try again.</EmptyState>
      )}
      {!isLoading && !isTruthy(queryError) && options.length > 0 && (
        <ScrollableList>
          <DropdownMenu.RadioGroup onValueChange={onSelect} value={value}>
            {options.map((option) => (
              <DropdownMenu.RadioItem
                key={option.externalID}
                trackingId={`${trackingIdPrefix}option`}
                value={option.name}
              >
                <DropdownMenu.ItemIndicator>
                  <DropdownMenu.IndicatorIcon />
                </DropdownMenu.ItemIndicator>
                {option.name}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </ScrollableList>
      )}
      {allowCustomValue && (
        <AddFooter>
          <DropdownMenu.Item
            key={`custom-${trimmedSearch}`}
            onSelect={() => onCustomValueSelect(trimmedSearch)}
            trackingId={`${trackingIdPrefix}add-custom`}
          >
            Add &quot;{trimmedSearch}&quot;
          </DropdownMenu.Item>
        </AddFooter>
      )}
    </TypeaheadDropdownMenuContent>
  );
};

// Flex-column layout so ScrollableList and AddFooter size independently within the capped portal.
const TypeaheadDropdownMenuContent = styled(DropdownMenu.Content)(
  () => `
    display: flex;
    flex-direction: column;
    max-height: ${DROPDOWN_MAX_HEIGHT};
    overflow: hidden;
  `,
);

// flex: 1 absorbs available portal height; min-height: 0 allows shrinking below content size.
const ScrollableList = styled.div(
  () => `
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  `,
);

// Sibling of ScrollableList — lives outside the scroll container so it's always visible.
const AddFooter = styled.div(
  ({ theme }) => `
    flex-shrink: 0;
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray3};
  `,
);

const EmptyState = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]}
      calc(${theme.spacings[2]} + ${theme.spacings[1]});
    color: ${theme.colors.gray5};
    text-align: center;
  `,
);
